/// <reference types="google.maps" />
import { Component, DestroyRef, ElementRef, OnInit, afterNextRender, inject, output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { OfficeQuotesService } from '@office/quotes/quotes.service';
import { PublicService } from '@public/public.service';
import { placeDataFields } from '@shared/constants/map-fields-constants';
import { parseGooglePlaceAddress } from '@shared/functions/utils.type';
import { Address } from '@shared/models/domain/domain.model';
import { AlertService } from '@shared/services/alert.service';
import { startWith } from 'rxjs';

@Component({
    selector: 'map-location',
    templateUrl: './map-location.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatInput,
        ReactiveFormsModule,
    ]
})
export class MapLocationComponent extends FieldType<FieldTypeConfig> implements OnInit {
    private officeQuotesService = inject(OfficeQuotesService);
    private publicService = inject(PublicService);
    private alertService = inject(AlertService);
    private destroyRef = inject(DestroyRef);

    searchElementRef = viewChild.required<ElementRef<HTMLDivElement>>('search');
    fetchAddress = output<Address>();

    searchControl = new FormControl('');
    isFieldRequired = false;
    private googleAddress: Address = {} as Address;
    private placeAutocomplete?: google.maps.places.PlaceAutocompleteElement;

    constructor() {
        super();
        afterNextRender(() => {
            google.maps.importLibrary('places')
                .then(({ PlaceAutocompleteElement }) => {
                    const placeAutocomplete = new PlaceAutocompleteElement();
                    placeAutocomplete.disabled = this.isDisabled();
                    placeAutocomplete.style.colorScheme = 'light';
                    placeAutocomplete.noClearButton = true;
                    placeAutocomplete.value = this.searchControl.value ?? '';
                    this.placeAutocomplete = placeAutocomplete;
                    this.searchElementRef().nativeElement.appendChild(placeAutocomplete);

                    placeAutocomplete.addEventListener('input', (event: Event) => {
                        this.searchControl.setValue(event.target instanceof HTMLInputElement ? event.target.value : '');
                        this.searchControl.markAsTouched();
                        this.formControl.markAsTouched();
                    });

                    placeAutocomplete.addEventListener('gmp-select', ({ placePrediction }) => {
                        this.selectPlace(placePrediction);
                    });
                })
                .catch(() => {
                    this.handlePlacesLoadError();
                });
        });

        this.destroyRef.onDestroy(() => this.placeAutocomplete?.remove());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this.field ??= {} as FieldType<FieldTypeConfig>;
        this.field.formControl ??= new FormControl();
        if (this.field?.props) {
            this.field.props.label ??= 'Search for Location';
        }

        this.isFieldRequired = (this.field.formControl?.hasValidator(Validators.required)) || !!this.field?.props?.required;
        if (this.isFieldRequired) {
            this.searchControl.setValidators([Validators.required]);
        }

        if (this.field?.props?.current) {
            this.searchControl.setValue(this.field?.props?.current as string);
        }

        this.field.formControl.statusChanges
            .pipe(
                startWith(this.field.formControl.status),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                const disabled = this.isDisabled();
                this.searchControl[disabled ? 'disable' : 'enable']({ emitEvent: false });
                if (this.placeAutocomplete) {
                    this.placeAutocomplete.disabled = disabled;
                }
            });

        this.searchControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.searchControl.disabled) return;
                this.searchControl.setErrors({ notSelectedOption: true });
                this.formControl.setValue(null);
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    clearSelection(): void {
        if (this.isDisabled()) return;
        this.searchControl.setValue('', { emitEvent: false });
        this.searchControl.markAsTouched();
        this.searchControl.updateValueAndValidity({ emitEvent: false });
        if (this.placeAutocomplete) {
            this.placeAutocomplete.value = '';
        }
        if (this.field?.props?.['storeAddressId'] && this.field?.parent?.model) {
            (this.field.parent.model as { address: string })['address'] = null;
        }
        this.formControl.setValue(null);
        this.formControl.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * This function is use for handle place details
     * @param place
     * @returns
     */
    handlePlaceDetails(place: google.maps.places.Place): void {
        if (this.isDisabled()) return;
        if (!place?.location) return;
        let address: Address;

        try {
            address = this.parseAddress(place);
        } catch {
            this.clearSelection();
            this.alertService.errorToast('Error', 'Unable to read the selected address. Please choose a full address from the list.');
            return;
        }

        this.searchControl.setErrors(null);
        this.searchControl.setValue(place.formattedAddress, { emitEvent: false });
        if (this.placeAutocomplete) {
            this.placeAutocomplete.value = place.formattedAddress ?? '';
        }
        this.fetchAddress.emit(address);

        if (this.field?.props?.['storeAddressId'] && this.officeQuotesService) {
            const tokenHash = this.field?.props?.['tokenHash'] as string;
            const isPublicPage = !!this.field?.props?.['isPublicPage'];
            const addressRequest$ = isPublicPage && tokenHash
                ? this.publicService.getPublicAddressByExternalId(tokenHash, address)
                : this.officeQuotesService.getAddressByExternalId(address);

            addressRequest$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (addr: Address) => {
                    if (addr?.addressId) {
                        if (this.field?.parent?.model != null) {
                            (this.field.parent.model as { address: string })['address'] = this.searchControl.value;
                        }
                        this.formControl.setValue(addr.addressId);
                        return;
                    }
                    this.clearSelection();
                },
                error: () => {
                    this.clearSelection();
                    this.alertService.errorToast('Error', 'Failed to fetch address details. Please try again.');
                }
            });
        } else {
            this.formControl.setValue(place.id);
        }
    }

    /**
     * This function is use for set map address
     * @param address
     */
    async setAddress(placeId: string): Promise<void> {
        try {
            const { Place } = await google.maps.importLibrary('places');
            const place = new Place({ id: placeId });
            await place.fetchFields({ fields: placeDataFields });
            this.handlePlaceDetails(place);
        } catch {
            this.handlePlacesLoadError();
        }
    }

    parseAddress(place: google.maps.places.Place): Address {
        this.googleAddress = parseGooglePlaceAddress(place);
        return this.googleAddress;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private async selectPlace(placePrediction: google.maps.places.PlacePrediction): Promise<void> {
        this.googleAddress = {} as Address;
        const place = placePrediction.toPlace();

        try {
            await place.fetchFields({ fields: placeDataFields });
        } catch {
            this.handlePlacesLoadError();
            return;
        }

        this.handlePlaceDetails(place);
    }

    private handlePlacesLoadError(): void {
        this.clearSelection();
        this.searchControl.setErrors({ placesUnavailable: true });
        this.alertService.errorToast('Error', 'Location search is currently unavailable. Please try again.');
    }

    private isDisabled(): boolean {
        return this.formControl?.disabled || !!this.field?.props?.disabled;
    }
}
